import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Stack, Typography } from "@altimateai/lego";
import { RelativeComponentLoader } from "@/components/loader";
import { FilterApplicableFor } from "@/components/tags/types";
import { useColumnPickerContext } from "@/components/column/types";
import { setApplicableFor } from "@/components/column/columnPickerSlice";
import { usePriorityOpportunitiesQuery } from "../../hooks";
import type { OwnerListItem } from "../../types";
import { PriorityTable } from "./PriorityTable";
import noPriorityOpportunities from "../../assets/no_priority_opportunities.svg";

export const PrioritySection = () => {
  const { data, isLoading, error, refetch } = usePriorityOpportunitiesQuery();
  const [ownerList, setOwnerList] = useState<OwnerListItem[] | null>(null);
  // Track opportunity_ids whose owner the user has changed locally so we
  // don't overwrite the optimistic value on the next background refetch.
  // After the mutation invalidates and the refetch returns the new owner
  // from the server, we mark it as clean and let the seed-effect sync.
  const dirtyOwners = useRef<Set<string>>(new Set());
  const { dispatch } = useColumnPickerContext();

  // Scope the Discover-page column picker to this tab so its
  // show/hide selections apply to the Priority table the same way
  // they apply to Opportunities.
  useEffect(() => {
    dispatch(setApplicableFor(FilterApplicableFor.DISCOVER));
  }, [dispatch]);

  // Sync the optimistic owner-list cache with server data, but never
  // overwrite a row whose owner the user just changed (until the
  // server-side mutation lands).
  useEffect(() => {
    if (!data) return;
    setOwnerList(prev => {
      const prevById = new Map(prev?.map(item => [item.opportunity_id, item.owner]));
      return data.map(item => {
        const localOwner = prevById.get(item.opportunity_id);
        const serverOwner = item.owner ?? "";
        if (dirtyOwners.current.has(item.opportunity_id)) {
          // Server caught up with the optimistic value — clear the dirty
          // flag so future refetches can sync legit server-side updates.
          if (String(localOwner ?? "") === String(serverOwner)) {
            dirtyOwners.current.delete(item.opportunity_id);
            return { opportunity_id: item.opportunity_id, owner: serverOwner };
          }
          return { opportunity_id: item.opportunity_id, owner: localOwner ?? "" };
        }
        return { opportunity_id: item.opportunity_id, owner: serverOwner };
      });
    });
  }, [data]);

  const handleOwnerChange = useCallback((owner: number | string, opportunity_id: string) => {
    dirtyOwners.current.add(opportunity_id);
    setOwnerList(
      prev =>
        prev?.map(item => (item.opportunity_id === opportunity_id ? { ...item, owner } : item)) ??
        null
    );
  }, []);

  if (isLoading) {
    return <RelativeComponentLoader label={"Loading priority opportunities"} />;
  }

  if (error) {
    return (
      <Stack direction={"column"} gap={"md"} align={"center"} className={"al-w-full al-py-12"}>
        <Typography variant="body">Could not load priority opportunities.</Typography>
        <Button size="sm" variant="ghost" onClick={() => refetch()}>
          Retry
        </Button>
      </Stack>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Stack direction={"column"} gap={"md"} align={"center"} className={"al-w-full al-py-12"}>
        <img src={noPriorityOpportunities} alt="" className="al-w-[228px] al-h-auto" />
        <Typography variant="h4" weight="medium" className="al-text-center">
          No prioritised opportunities detected!
        </Typography>
        <Typography variant="body" className="al-text-center">
          Please reach out to an Altimate representative to know more!
        </Typography>
        <Button variant="outline" onClick={() => window.Intercom?.("show")}>
          Contact Us
        </Button>
      </Stack>
    );
  }

  return <PriorityTable rows={data} ownerList={ownerList} onOwnerChange={handleOwnerChange} />;
};
